import React, { useState, useEffect, useMemo } from 'react'
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  Stack,
  useTheme,
  useMediaQuery,
  Tooltip,
  keyframes,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableBody,
} from '@mui/material'
import {
  Check,
  Lock,
  Speed,
  Support,
  ExpandMore,
  AccountBalance,
  Business,
  Email,
  Public,
} from '@mui/icons-material'
import EmojiObjectsRoundedIcon from '@mui/icons-material/EmojiObjectsRounded'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import apiService from '../services/ApiService'
import { StaticPageWrapper } from './RouteWrappers'
import { getSEOForRoute } from '../utils/seoConfig'
// Theme colors
const themeColors = {
  primary: '#232536',
  secondary: '#ffcf51',
  hover: '#3a3e5a',
  success: '#43af76',
}
// Blinking animation for FREE FOREVER
const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`
// Country display names
const COUNTRY_DISPLAY_NAMES = {
  GCC: 'All GCC Countries',
  UAE: 'UAE',
  KSA: 'KSA',
  Kuwait: 'Kuwait',
  Qatar: 'Qatar',
  Oman: 'Oman',
  Bahrain: 'Bahrain',
}
const SLASHED_PRICE_CONFIG = {
  GCC: {
    COMPLETE: {
      DAILY: 149.99,
      MONTHLY: 499.99,
      // ANNUAL: 3499.99,
    },
    ENTERPRISE: {
      ANNUAL: 29999.99,
    },
  },
  COUNTRY: {
    COMPLETE: {
      DAILY: 99.99,
      MONTHLY: 299.99,
      // ANNUAL: 2499.99,
    },
    ENTERPRISE: {
      ANNUAL: 23999.99,
    },
  },
}
const getDomainPriceKey = (isGccDomain) => (isGccDomain ? 'GCC' : 'COUNTRY')
const AED_NUMBER_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})
const formatAedNumber = (amount) =>
  AED_NUMBER_FORMATTER.format(Number(amount || 0))
const formatAed = (amount) => `AED ${formatAedNumber(amount)}`
const getFeatureComparisonData = (domainName, isGccDomain) => {
  return [
    {
      feature: isGccDomain ? 'GCC Country Tax Laws' : `${domainName} Tax Laws`,
      free: '✓',
      // allAccess: isGccDomain ? 'All 6 Countries' : domainName,
      allAccess: '✓',
      dtaa: '✓',
    },
    {
      feature: 'DTAA / Tax Treaty Views',
      free: '1/month',
      allAccess: 'Unlimited',
      dtaa: 'Unlimited',
    },
    {
      feature: 'Document Link Clicks',
      free: '10/month',
      allAccess: 'Unlimited',
      dtaa: '10/month',
    },
    {
      feature: 'Advanced Search',
      free: '✗',
      allAccess: '✓',
      dtaa: '✓',
    },
    {
      feature: 'Download & Print documents with linking',
      free: '✗',
      allAccess: '✓',
      dtaa: '✗',
    },
    {
      feature: 'Priority Email Support',
      free: '✗',
      allAccess: '✓',
      dtaa: '✓',
    },
  ]
}
const Pricing = () => {
  const seoData = getSEOForRoute(window.location.pathname)
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, user, quotaStatus } = useAuth()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.down('md'))
  const [groupedPlans, setGroupedPlans] = useState(null)
  const [selectedTab, setSelectedTab] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // Fetch plans from backend
  useEffect(() => {
    const fetchPricingData = async () => {
      try {
        setLoading(true)
        // Fetch grouped plans based on domain
        const response = await apiService.get('/api/v1/subscriptions/plans')
        console.log('Grouped plans received:', response)
        setGroupedPlans(response)

        setError(null)
      } catch (err) {
        console.error('Error fetching pricing data:', err)
        setError('Failed to load pricing information. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    fetchPricingData()
  }, [])
  const handlePlanSelect = (planCode) => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/pricing', planCode } })
      return
    }
    navigate(`/checkout?plan=${encodeURIComponent(planCode)}`)
  }
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue)
  }
  // Determine if we're in ALL mode (localhost with grouped plans)
  const isAllMode = groupedPlans?.domain === 'ALL'
  const isGccDomain = groupedPlans?.domain === 'GCC'
  const isCountrySpecific = Boolean(groupedPlans) && !isAllMode && !isGccDomain
  // Get current plans to display
  const currentPlans = useMemo(() => {
    if (!groupedPlans) return []
    if (isAllMode && groupedPlans.groups) {
      // Localhost with tabs - return plans from selected tab
      return groupedPlans.groups[selectedTab]?.plans || []
    } else {
      // Single domain - return all plans
      return groupedPlans.plans || []
    }
  }, [groupedPlans, selectedTab, isAllMode])
  // Get current domain/country info
  const currentDomainInfo = useMemo(() => {
    if (!groupedPlans) return { key: '', name: '', description: '' }
    if (isAllMode && groupedPlans.groups) {
      const group = groupedPlans.groups[selectedTab]
      return {
        key: group?.groupKey || '',
        name: group?.groupName || '',
        description: group?.groupDescription || '',
      }
    } else {
      return {
        key: groupedPlans.domain,
        name: COUNTRY_DISPLAY_NAMES[groupedPlans.domain] || groupedPlans.domain,
        description: '',
      }
    }
  }, [groupedPlans, selectedTab, isAllMode])
  const isCurrentDomainGcc = currentDomainInfo.key === 'GCC'
  // Categorize plans
  const categorizedPlans = useMemo(() => {
    const daily = currentPlans.filter((p) => p.durationType === 'DAILY')
    const monthly = currentPlans.filter((p) => p.durationType === 'MONTHLY')
    const annual = currentPlans.filter((p) => p.durationType === 'ANNUAL')
    // Separate by type
    const completePlans = currentPlans.filter(
      (p) =>
        p.planType === 'ALL_COUNTRIES' || p.planType === 'COUNTRY_SPECIFIC',
    )
    const dtaaPlans = currentPlans.filter((p) => p.planType === 'DTAA_ONLY')
    const enterprisePlans = currentPlans.filter((p) => p.isEnterprise)

    return {
      daily,
      monthly,
      annual,
      complete: completePlans.filter((p) => !p.isEnterprise),
      dtaa: dtaaPlans.filter((p) => !p.isEnterprise),
      enterprise: enterprisePlans,
    }
  }, [currentPlans])
  // Get hero subtitle based on domain
  const getHeroSubtitle = () => {
    if (isGccDomain) {
      return 'Get complete access to all GCC tax laws and tax treaties (as and when made available on the portal)'
    } else if (isCountrySpecific) {
      return `Get complete access to ${currentDomainInfo.name} tax laws and tax treaties (as and when made available on the portal)`
    } else {
      return 'Choose your country to see tailored pricing plans'
    }
  }
  // Free plan features (always the same)
  const freePlanFeatures = [
    '10 document link clicks per month',
    '1 DTAA / Tax Treaty document view per month',
    'Basic search functionality',
    'Bookmark articles',
    'Add annotations',
    'Email support',
  ]
  // Get features for complete access plans
  const getCompleteAccessFeatures = () => {
    if (isGccDomain) {
      return [
        'Unlimited access to ALL 6 GCC countries',
        'Unlimited DTAA / Tax Treaty access for all 6 GCC countries',
        'Advanced search & filters',
        'Share, download & print documents',
        'Bookmark & annotate',
        'Priority email support',
      ]
    } else {
      return [
        `Unlimited access to ${currentDomainInfo.name} tax laws`,
        `Unlimited ${currentDomainInfo.name} DTAA / Tax Treaty access`,
        'Advanced search & filters',
        'Share, download & print documents',
        'Bookmark & annotate',
        'Priority email support',
      ]
    }
  }
  // DTAA features
  const dtaaFeatures = [
    'Unlimited DTAA / Tax Treaty access',
    'Country comparison tools',
    'Share, download & print DTAA / Tax Treaty',
    'Bookmark & annotate',
    'Priority email support',
  ]
  // FAQs - dynamically generated
  const faqs = useMemo(() => {
    const domainName = currentDomainInfo.name

    return [
      {
        question: isGccDomain
          ? 'What does the All Access Pass include?'
          : `What does the ${domainName} Access Pass include?`,
        answer: isGccDomain
          ? "The All Access Pass gives you unlimited access to domestic tax laws of all 6 GCC countries (UAE, KSA, Kuwait, Qatar, Oman, Bahrain) PLUS unlimited access to all Double Taxation Avoidance Agreements (DTAA / Tax Treaty). It's our most comprehensive offering - everything you need in one plan."
          : `The ${domainName} Access Pass gives you unlimited access to ${domainName} domestic tax laws PLUS unlimited access to all ${domainName} Double Taxation Avoidance Agreements (DTAA / Tax Treaty). It's our comprehensive offering for ${domainName}-focused professionals.`,
      },
      // {
      //   question:
      //     "What's the difference between Complete Access and DTAA Module?",
      //   answer:
      //     'Complete Access includes domestic tax laws AND all tax treaties. The DTAA Module includes ONLY tax treaties (DTAA documents) along with the Free version features (10 document links per month).',
      // },
      {
        question: 'Can I try before committing to a subscription?',
        answer: isGccDomain
          ? 'Absolutely! Try our 1-Day All Access Pass for just AED 49.99. You get 24 hours of complete unlimited access to tax laws across all 6 GCC countries and all Tax Treaties.'
          : `Absolutely! Try our 1-Day ${domainName} Access Pass for just AED 29.99. You get 24 hours of complete unlimited access to all ${domainName} features and tax laws.`,
      },
      {
        question: 'What happens when my free quota runs out?',
        answer:
          'When you exhaust your free quota, you can wait for the reset on the 1st of next month, get a 1-Day Pass, or upgrade to a subscription for unlimited access.',
      },
      {
        question: 'Do subscriptions auto-renew?',
        answer:
          'Yes, monthly and annual subscriptions automatically renew unless you cancel. You can manage this anytime from your profile. The 1-Day Pass does not auto-renew.',
      },
      {
        question: 'Can I cancel my subscription anytime?',
        answer:
          'Absolutely! Cancel anytime from your profile. You will continue to have access until the end of your current billing period. No refunds for partial periods.',
      },
      {
        question: 'What is the Enterprise Subscription?',
        answer:
          'Our Enterprise plan is designed for teams. Each subscription includes unrestricted access for up to 7 users, dedicated account management, and priority support. Contact us at support@gcctaxlaws.com for details.',
      },
    ]
  }, [currentDomainInfo.name, isGccDomain])
  if (loading) {
    return (
      <Container maxWidth='lg' sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant='h6' sx={{ mt: 3 }}>
          Loading pricing information...
        </Typography>
      </Container>
    )
  }
  if (error) {
    return (
      <Container maxWidth='lg' sx={{ py: 8 }}>
        <Alert severity='error'>{error}</Alert>
        <Button
          variant='contained'
          onClick={() => window.location.reload()}
          sx={{
            mt: 2,
            // Default State
            bgcolor: '#232536',
            color: '#ffffff',
            borderRadius: 2, // Matches your other buttons
            textTransform: 'none', // Prevents all-caps if desired

            '&:hover': {
              // Option A: The Yellow Hover from your prompt
              bgcolor: '#ffcf51',
              color: '#232536',

              // Option B: The Darker Blue (#3a3e5a)
              // If you prefer the dark blue hover, swap the bgcolor above to:
              // bgcolor: '#3a3e5a',
              // color: '#ffffff',
            },
          }}
        >
          Retry
        </Button>
      </Container>
    )
  }
  return (
    <>
      <StaticPageWrapper useDomainSEO />
      <Box
        sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', py: { xs: 4, md: 8 } }}
      >
        <Container maxWidth='lg'>
          {/* Hero Section */}
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
            <Typography
              variant='h1'
              sx={{
                fontWeight: 700,
                mb: 2,
                color: themeColors.primary,
                fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
              }}
            >
              Pricing Plans & Subscriptions
            </Typography>
            <Typography
              variant='h4'
              sx={{
                fontWeight: 700,
                mb: 1,
                color: themeColors.primary,
                fontSize: { xs: '1.5rem', md: '2rem' },
              }}
            >
              Simple, transparent pricing. Cancel anytime.
            </Typography>

            <Typography
              variant='h2'
              sx={{
                color: 'text.secondary',
                mb: 4,
                maxWidth: 700,
                mx: 'auto',
                fontSize: { xs: '1rem', md: '1.25rem' },
                px: { xs: 2, md: 0 },
              }}
            >
              {getHeroSubtitle()}
            </Typography>

            {/* Current Quota Status */}
            {isAuthenticated && quotaStatus && (
              <Alert
                severity='info'
                sx={{
                  maxWidth: 600,
                  mx: 'auto',
                  mb: 4,
                }}
              >
                <Box sx={{ textAlign: 'left', width: '100%' }}>
                  <Typography variant='body2' sx={{ fontWeight: 600, mb: 1 }}>
                    Your Current Status:
                  </Typography>
                  <Typography variant='body2'>
                    Link Clicks: {quotaStatus.remainingLinkClicks || 0} /{' '}
                    {quotaStatus.monthlyLinkClicksLimit || 0} remaining
                    <br />
                    DTAA Views: {quotaStatus.remainingDtaaViews || 0} /{' '}
                    {quotaStatus.monthlyDtaaViewsLimit || 0} remaining
                    {quotaStatus.hasActiveSubscription && (
                      <>
                        <br />
                        <strong>
                          ✓ You have {quotaStatus.activeSubscriptionsCount}{' '}
                          active subscription(s)
                        </strong>
                      </>
                    )}
                  </Typography>
                </Box>
              </Alert>
            )}
          </Box>

          {/* Tabs for localhost ALL mode */}
          {isAllMode && groupedPlans.groups && (
            <Box sx={{ mb: 4 }}>
              <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Tabs
                  value={selectedTab}
                  onChange={handleTabChange}
                  variant={isMobile ? 'scrollable' : 'fullWidth'}
                  scrollButtons={isMobile ? 'auto' : false}
                  sx={{
                    bgcolor: 'white',
                    '& .MuiTab-root': {
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      py: 2,
                    },
                    '& .Mui-selected': {
                      color: themeColors.primary,
                    },
                  }}
                >
                  {groupedPlans.groups.map((group, index) => (
                    <Tab
                      key={group.groupKey}
                      label={
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          {group.groupKey === 'GCC' ? (
                            <Public fontSize='small' />
                          ) : (
                            <AccountBalance fontSize='small' />
                          )}
                          {group.groupName}
                          <Chip
                            label={group.planCount}
                            size='small'
                            sx={{
                              bgcolor: themeColors.secondary,
                              color: themeColors.primary,
                              fontWeight: 700,
                              height: 20,
                              fontSize: '0.7rem',
                            }}
                          />
                        </Box>
                      }
                    />
                  ))}
                </Tabs>
              </Paper>

              {/* Tab Description */}
              {currentDomainInfo.description && (
                <Alert severity='info' sx={{ mt: 2 }}>
                  {currentDomainInfo.description}
                </Alert>
              )}
            </Box>
          )}

          {/* Free Plan Callout */}
          <Paper
            sx={{
              p: { xs: 2, md: 3 },
              mb: 6,
              bgcolor: themeColors.primary, // Entire box background
              border: `2px solid ${themeColors.secondary}`, // Border using secondary for contrast
              borderRadius: 2,
              color: 'white', // Base font color for the whole box
            }}
          >
            <Grid container spacing={3} alignItems='center'>
              <Grid item xs={12} md={8}>
                <Chip
                  label='Free Plan Available'
                  sx={{
                    bgcolor: themeColors.secondary, // Background secondary
                    color: themeColors.primary, // Font primary
                    fontWeight: 700,
                    mb: 2,
                    px: 2,
                  }}
                />
                <Typography
                  variant='body1'
                  sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.9)' }} // Slightly softer white
                >
                  Get started with <strong>10 link clicks</strong> and{' '}
                  <strong>1 DTAA / Tax Treaty view</strong> every calendar month
                  at no cost. Perfect for occasional users.
                </Typography>
                <List dense>
                  {freePlanFeatures.slice(0, 3).map((feature, index) => (
                    <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Check
                          sx={{ color: themeColors.secondary, fontSize: 20 }} // Changed check to secondary for visibility
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={feature}
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: 'white',
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>

              <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                <Typography
                  variant='h3'
                  sx={{
                    fontWeight: 700,
                    color: themeColors.secondary, // Blinks with secondary color
                    mb: 2,
                    fontSize: { xs: '2rem', md: '2.5rem' },
                    animation: `${blink} 2s ease-in-out infinite`,
                  }}
                >
                  FREE FOREVER
                </Typography>
                {!isAuthenticated && (
                  <Button
                    variant='contained'
                    size='large'
                    onClick={() => navigate('/login')}
                    fullWidth={isMobile}
                    sx={{
                      bgcolor: themeColors.secondary, // Background secondary
                      color: themeColors.primary, // Font primary
                      px: 4,
                      py: 1.5,
                      fontWeight: 800,
                      '&:hover': {
                        bgcolor: '#e6ba49', // A slightly darker shade of your secondary for hover
                      },
                    }}
                  >
                    Get Started
                  </Button>
                )}
              </Grid>
            </Grid>
          </Paper>
          {/* Complete Access Plans */}
          {categorizedPlans.complete.length > 0 && (
            <PlanSection
              title={
                isGccDomain
                  ? 'All GCC Countries Access'
                  : `${currentDomainInfo.name} Complete Access`
              }
              description={
                isGccDomain
                  ? 'Complete unlimited access to all 6 GCC countries tax laws AND all DTAA / tax treaties'
                  : `Complete unlimited access to ${currentDomainInfo.name} tax laws AND all ${currentDomainInfo.name} DTAA / tax treaties`
              }
              features={getCompleteAccessFeatures()}
              plans={categorizedPlans.complete}
              onSelectPlan={handlePlanSelect}
              isMobile={isMobile}
              isGccDomain={isCurrentDomainGcc}
            />
          )}
          {/* Cross-linking based on domain */}
          <Box sx={{ my: 6 }}>
            <CrossLinkingSection
              isGccDomain={isGccDomain}
              isCountrySpecific={isCountrySpecific}
            />
          </Box>

          {/* DTAA Only Plans */}
          {categorizedPlans.dtaa.length > 0 && (
            <PlanSection
              title='DTAA / Tax Treaty Module'
              description='Unlimited access to all DTAA / Tax Treaties + Free plan features'
              features={dtaaFeatures}
              plans={categorizedPlans.dtaa}
              onSelectPlan={handlePlanSelect}
              isMobile={isMobile}
              isGccDomain={isCurrentDomainGcc}
              icon={<AccountBalance />}
            />
          )}

          {/* Enterprise Plans */}
          {categorizedPlans.enterprise.length > 0 && (
            <EnterpriseSection
              plans={categorizedPlans.enterprise}
              onSelectPlan={handlePlanSelect}
              domainInfo={currentDomainInfo}
              isGccDomain={isCurrentDomainGcc}
              isMobile={isMobile}
            />
          )}

          {/* Feature Comparison Table */}
          <Box sx={{ mt: 8, mb: 6 }}>
            <Typography
              variant='h4'
              sx={{
                fontWeight: 700,
                mb: 4,
                textAlign: 'center',
                color: '#232536',
                fontSize: { xs: '1.5rem', md: '2rem' },
              }}
            >
              Compare Features
            </Typography>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                border: '1px solid rgba(35, 37, 54, 0.2)',
                bgcolor: 'white',
              }}
            >
              <FeatureComparisonTable
                isMobile={isMobile}
                isTablet={isTablet}
                domainName={currentDomainInfo?.name || 'Complete'}
                isGccDomain={isGccDomain}
                sx={{
                  // 1. FIRST ROW HEADER (Dark background, White font)
                  '& thead th': {
                    bgcolor: '#232536',
                    color: 'white', // Font white
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    py: 3,
                    borderRight: '1px solid rgba(255, 255, 255, 0.1)',
                    borderBottom: '2px solid #ffcf51',
                    textAlign: 'center',
                    '&:first-of-type': {
                      textAlign: 'center',
                      px: 3,
                      color: '#ffda1b',
                    },
                    '&:last-child': { borderRight: 'none' },
                  },

                  // 2. FIRST COLUMN HEADER (Dark background, White font)
                  '& td:first-of-type': {
                    bgcolor: '#232536', // Dark background to support white text
                    color: '#ffda1b',
                    fontWeight: 600,
                    px: 3,
                    textAlign: 'center',
                    borderRight: '2px solid rgba(255, 255, 255, 0.1)',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                  },

                  // 3. VERTICAL LINES & DATA CELLS
                  '& td': {
                    py: 2.5,
                    textAlign: 'center',
                    color: '#232536',
                    borderRight: '1px solid rgba(35, 37, 54, 0.1)', // Vertical separator
                    borderBottom: '1px solid rgba(35, 37, 54, 0.1)',
                    '&:last-child': { borderRight: 'none' },
                  },

                  // 4. STRIPING (Light grey for contrast)
                  '& tbody tr:nth-of-type(even) td:not(:first-of-type)': {
                    bgcolor: 'rgba(35, 37, 54, 0.03)',
                  },
                }}
              />
            </Paper>
          </Box>
          {/* FAQ Section */}
          <Box sx={{ mt: 8 }}>
            <Typography
              variant='h4'
              sx={{
                fontWeight: 600,
                mb: 4,
                textAlign: 'center',
                fontSize: { xs: '1.5rem', md: '2rem' },
              }}
            >
              Frequently Asked Questions
            </Typography>
            {faqs.map((faq, index) => (
              <Accordion
                key={index}
                sx={{
                  mb: 2,
                  boxShadow: 1,
                  '&:before': { display: 'none' },
                  borderRadius: 2,
                }}
                // Lazy loading - only expand on user interaction
                TransitionProps={{ unmountOnExit: true }}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant='body2' color='text.secondary'>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          {/* Trust Signals */}
          <TrustSignals isMobile={isMobile} />
        </Container>
      </Box>
    </>
  )
}
// Plan Section Component
const PlanSection = ({
  title,
  description,
  features,
  plans,
  onSelectPlan,
  isMobile,
  isGccDomain,
  icon = null,
}) => {
  return (
    <Box sx={{ mb: 8 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: 3,
          bgcolor: themeColors.primary, // Dark #232536
          border: `2px solid ${themeColors.secondary}`, // Yellow #ffcf51
          color: 'white',
        }}
      >
        <Grid container spacing={4}>
          {/* Left Side - Features */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              {icon &&
                React.cloneElement(icon, {
                  sx: { color: themeColors.secondary, mr: 1 },
                })}
              <Typography
                variant='h4'
                sx={{
                  fontWeight: 700,
                  color: 'white',
                  fontSize: { xs: '1.5rem', md: '2.3rem' },
                }}
              >
                {title}
              </Typography>
            </Box>
            <Typography
              variant='body1'
              sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.9)', lineHeight: 1.6 }}
            >
              {description}
            </Typography>

            <Typography
              variant='h6'
              sx={{
                fontWeight: 700,
                mt: 3,
                mb: 1,
                color: themeColors.secondary, // Use yellow instead of success green
              }}
            >
              What's Included:
            </Typography>

            <List>
              {features.map((feature, index) => (
                <ListItem key={index} sx={{ px: 0, py: 0.8 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {/* Replaced success color with secondary yellow */}
                    <Check
                      sx={{ color: themeColors.secondary, fontSize: 24 }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={feature}
                    primaryTypographyProps={{
                      variant: 'body1',
                      fontWeight: 500,
                      color: 'white', // Ensure text is pure white
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>

          {/* Right Side - Pricing Options */}
          <Grid item xs={12} md={6}>
            <Stack
              spacing={3}
              sx={{ height: '100%', justifyContent: 'center' }}
            >
              {[...plans]
                .sort((a, b) => {
                  const order = { ANNUAL: 1, MONTHLY: 2, DAILY: 3 }
                  return order[a.durationType] - order[b.durationType]
                })
                .map((plan) => (
                  <PlanCard
                    key={plan.id}
                    plan={plan}
                    onSelect={onSelectPlan}
                    isMobile={isMobile}
                    isGccDomain={isGccDomain}
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                      // Target the button inside the PlanCard
                      '& .MuiButton-contained': {
                        bgcolor: themeColors.secondary, // Yellow #ffcf51
                        color: themeColors.primary, // Dark #232536
                        fontWeight: 800,
                        px: 3,
                        '&:hover': {
                          bgcolor: '#e6ba49', // Slightly darker yellow on hover
                        },
                      },
                      // Fix readability for unreadable grey text (pricing details)
                      '& .MuiTypography-root': { color: 'white' },
                      '& .MuiTypography-caption': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                      // If there is success green text anywhere, turn it yellow
                      '& .text-success': { color: themeColors.secondary },
                    }}
                  />
                ))}
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  )
}
// Plan Card Component
const PlanCard = ({ plan, onSelect, isMobile, isGccDomain, sx = {} }) => {
  const [showTooltip, setShowTooltip] = useState(false)

  const getBillingText = () => {
    switch (plan.durationType) {
      case 'DAILY':
        return '/day'
      case 'MONTHLY':
        return '/month'
      case 'ANNUAL':
        return '/year'
      default:
        return ''
    }
  }

  const isDayPass = plan.durationType === 'DAILY'

  const domainPriceKey = getDomainPriceKey(isGccDomain)
  const isCompleteAccessPlan =
    plan.planType === 'ALL_COUNTRIES' || plan.planType === 'COUNTRY_SPECIFIC'
  const customSlashedPrice = isCompleteAccessPlan
    ? SLASHED_PRICE_CONFIG[domainPriceKey]?.COMPLETE?.[plan.durationType]
    : null
  const slashedPrice = customSlashedPrice ?? plan.priceAed * 2
  const formattedSlashedPrice = formatAed(slashedPrice)

  // Calculate per-day rate (hide helper text for 1-day pass)
  const perDayRate = plan.priceAed / plan.durationDays
  const formattedPerDayRate = `${formatAed(perDayRate)}/day`
  const showPerDayRate = Number(plan.durationDays) > 1

  // Get plan title
  const getPlanTitle = () => {
    if (isDayPass) {
      return '1-Day Pass'
    }
    return `${plan.displayDuration} Subscription`
  }

  return (
    <Tooltip
      title={
        <Box sx={{ p: 1 }}>
          <Typography variant='body2' sx={{ mb: 1, fontWeight: 600 }}>
            {plan.description}
          </Typography>
          {plan.isRecurring && !isDayPass && (
            <Typography variant='caption'>
              Auto-renews • Cancel anytime
            </Typography>
          )}
          {isDayPass && (
            <Typography variant='caption'>
              One-time purchase • No auto-renewal
            </Typography>
          )}
        </Box>
      }
      arrow
      placement={isMobile ? 'top' : 'left'}
      open={showTooltip}
      PopperProps={{
        modifiers: [
          { name: 'offset', options: { offset: [0, 8] } },
          {
            name: 'flip',
            options: {
              fallbackPlacements: isMobile
                ? ['bottom', 'top']
                : ['right', 'bottom', 'top'],
            },
          },
          {
            name: 'preventOverflow',
            options: {
              boundary: 'viewport',
              padding: 12,
            },
          },
        ],
      }}
      componentsProps={{
        tooltip: {
          sx: {
            maxWidth: { xs: 'calc(100vw - 24px)', sm: 320 },
          },
        },
      }}
    >
      <Paper
        elevation={plan.badge ? 4 : 2}
        sx={{
          p: 2,
          border: plan.isBestValue
            ? `3px solid ${themeColors.secondary}`
            : '1px solid #e0e0e0',
          borderRadius: 2,
          cursor: 'pointer',
          transition: 'transform 0.2s, box-shadow 0.2s',
          backgroundColor: 'white',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 6,
          },
          position: 'relative',
          ...sx,
        }}
        onClick={() => onSelect(plan.planCode || plan.id)}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {plan.badge && (
          <Chip
            label={plan.badge}
            size='small'
            sx={{
              position: 'absolute',
              top: -12,
              left: 16,
              bgcolor: themeColors.secondary,
              color: themeColors.primary,
              fontWeight: 700,
              fontSize: '0.7rem',
            }}
          />
        )}
        <Stack
          direction={isMobile ? 'column' : 'row'}
          alignItems={isMobile ? 'stretch' : 'center'}
          justifyContent='space-between'
          spacing={isMobile ? 1.5 : 0}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant='subtitle1'
              sx={{ fontWeight: 700, color: themeColors.primary }}
            >
              {getPlanTitle()}
            </Typography>

            {/* Price with slashed original price */}
            <Stack spacing={0.25} sx={{ mt: 1 }}>
              {/* Row 1: Slashed Price */}
              <Typography
                variant='body2'
                sx={{
                  textDecoration: 'line-through',
                  color: 'text.disabled',
                  fontWeight: 500,
                }}
              >
                {formattedSlashedPrice}
              </Typography>

              {/* Row 2: Actual Price + Billing Text */}
              <Stack direction='row' alignItems='baseline' spacing={1}>
                <Typography
                  variant='h5'
                  sx={{ fontWeight: 700, color: themeColors.primary }}
                >
                  {formatAed(plan.priceAed)}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {getBillingText()}
                </Typography>
              </Stack>
            </Stack>

            {/* Per-day rate */}
            {showPerDayRate && (
              <Typography
                variant='caption'
                sx={{
                  display: 'block',
                  mt: 0.5,
                  color: themeColors.success,
                  fontWeight: 600,
                }}
              >
                {formattedPerDayRate}
              </Typography>
            )}

            {/* Savings Text */}
            {plan.savingsText && (
              <Chip
                label={plan.savingsText}
                size='small'
                sx={{
                  mt: 1,
                  bgcolor: themeColors.secondary,
                  color: themeColors.primary,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                }}
              />
            )}
          </Box>

          <Button
            variant='contained'
            sx={{
              bgcolor: themeColors.primary,
              color: 'white',
              fontWeight: 700,
              px: 3,
              py: 1,
              width: isMobile ? '100%' : 'auto',
              '&:hover': {
                bgcolor: themeColors.hover,
              },
            }}
          >
            {isDayPass ? 'Get Pass' : 'Subscribe'}
          </Button>
        </Stack>
      </Paper>
    </Tooltip>
  )
}

// Cross-Linking Section
const CrossLinkingSection = ({ isGccDomain, isCountrySpecific }) => {
  if (!isGccDomain && !isCountrySpecific) return null
const invertButtonStyle = {
  // DEFAULT STATE (Solid Yellow)
  bgcolor: themeColors.secondary, // Yellow Background
  color: themeColors.primary, // Dark Font
  fontWeight: 700,
  px: 3,
  py: 1,
  border: `1px solid ${themeColors.secondary}`, // Matches BG so it looks clean

  '&:hover': {
    // HOVER STATE (Solid Dark)
    bgcolor: themeColors.primary, // Becomes Dark
    color: themeColors.secondary, // Font becomes Yellow
    borderColor: themeColors.secondary, // Optional: add yellow border on hover
  },
}
  return (
    <Paper
      elevation={0}
      sx={{
        mt: 3,
        mb: 3,
        p: { xs: 3, md: 4 },
        bgcolor: themeColors.primary,
        borderRadius: 3,
        border: `1px solid ${themeColors.secondary}`,
      }}
    >
      <Box>
        <Typography
          variant='h6'
          sx={{
            mb: 1,
            fontWeight: 700,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          {/* THE ICON REPLACING THE EMOJI */}
          <EmojiObjectsRoundedIcon
            sx={{ color: themeColors.secondary, fontSize: '1.5rem' }}
          />

          {isCountrySpecific
            ? 'Looking for access to all 6 GCC countries?'
            : 'Need access to just one country?'}
        </Typography>

        <Typography
          variant='body2'
          sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.7)' }}
        >
          {isCountrySpecific
            ? 'For comprehensive access to UAE, KSA, Kuwait, Qatar, Oman, and Bahrain tax laws:'
            : 'For individual country-specific plans:'}
        </Typography>

        {isCountrySpecific ? (
          <Button
            variant='outlined'
            href='https://gcctaxlaws.com/pricing'
            target='_blank'
            rel='noopener noreferrer'
            sx={invertButtonStyle}
          >
            View All GCC Plans →
          </Button>
        ) : (
          <Grid container spacing={2}>
            {[
              { name: 'UAE', url: 'https://uaetaxlaws.com/pricing' },
              { name: 'KSA', url: 'https://ksataxlaws.com/pricing' },
              { name: 'Kuwait', url: 'https://kuwaittaxlaws.com/pricing' },
              { name: 'Qatar', url: 'https://qatartaxlaws.com/pricing' },
              { name: 'Oman', url: 'https://omantaxlaws.com/pricing' },
              { name: 'Bahrain', url: 'https://bahraintaxlaws.com/pricing' },
            ].map((country) => (
              <Grid item xs={6} sm={4} md={2} key={country.name}>
                <Button
                  variant='contained'
                  size='small'
                  fullWidth
                  href={country.url}
                  target='_blank'
                  rel='noopener noreferrer'
                  sx={invertButtonStyle}
                >
                  {country.name}
                </Button>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Paper>
  )
}
// Enterprise Section Component
const EnterpriseSection = ({
  plans,
  onSelectPlan,
  domainInfo,
  isGccDomain,
  isMobile,
}) => {
  const enterprisePlan = plans[0] // Assume one enterprise plan per domain
  if (!enterprisePlan) return null
  const domainPriceKey = getDomainPriceKey(isGccDomain)
  const enterpriseCustomSlashedPrice =
    SLASHED_PRICE_CONFIG[domainPriceKey]?.ENTERPRISE?.[
      enterprisePlan.durationType
    ]
  const enterpriseSlashedPrice =
    enterpriseCustomSlashedPrice ?? enterprisePlan.priceAed * 2
  return (
    <Box sx={{ mt: 8, mb: 6 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography
          variant='h4'
          sx={{
            fontWeight: 700,
            mb: 2,
            color: themeColors.primary,
            fontSize: { xs: '1.5rem', md: '2rem' },
          }}
        >
          <Business sx={{ mr: 1, verticalAlign: 'middle' }} />
          Enterprise Database Subscriptions
        </Typography>
      </Box>
      <Grid container spacing={3}>
        {/* Left Side - Description & Features */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={3}
            sx={{
              p: { xs: 3, md: 4 },
              height: '100%',
              background: 'linear-gradient(135deg, #232536 0%, #1a1c2e 100%)',
              color: 'white',
              borderRadius: 3,
            }}
          >
            <Typography variant='h5' sx={{ fontWeight: 700, mb: 4 }}>
              {enterprisePlan.description}
            </Typography>

            <List>
              {enterprisePlan.features.map((feature, index) => (
                <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Check
                      sx={{ color: themeColors.secondary, fontSize: 24 }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={feature}
                    primaryTypographyProps={{
                      variant: 'body1',
                      sx: { color: 'white' },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Right Side - Pricing & Contact */}
        <Grid item xs={12} md={6}>
          <Stack spacing={3}>
            {/* Pricing Box */}
            <Paper
              elevation={3}
              sx={{
                p: { xs: 3, md: 4 },
                background: 'linear-gradient(135deg, #232536 0%, #1a1c2e 100%)',
                color: 'white',
                borderRadius: 3,
                textAlign: 'center',
                overflow: 'hidden',
              }}
            >
              <Box>
                <Typography
                  variant='h6'
                  sx={{
                    mb: 2,
                    opacity: 0.85,
                    fontSize: { xs: '1.35rem', sm: '1.5rem', md: '1.75rem' },
                  }}
                >
                  Starting from
                </Typography>

                {/* Slashed Price */}
                <Typography
                  variant='h4'
                  sx={{
                    textDecoration: 'line-through',
                    color: 'rgba(255, 255, 255, 0.5)',
                    mb: 1,
                    fontSize: { xs: '1.6rem', sm: '2rem', md: '2.25rem' },
                    lineHeight: 1.2,
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                  }}
                >
                  {formatAed(enterpriseSlashedPrice)}/year
                </Typography>

                {/* Actual Price */}
                <Typography
                  variant='h2'
                  sx={{
                    fontWeight: 700,
                    color: themeColors.secondary,
                    mb: 1,
                    fontSize: { xs: '2rem', sm: '2.8rem', md: '3.75rem' },
                    lineHeight: 1.1,
                    overflowWrap: 'anywhere',
                    // wordBreak: 'break-word',
                  }}
                >
                  {formatAed(enterprisePlan.priceAed)}
                  <Box
                    component='span'
                    sx={{
                      display: { xs: 'block', sm: 'inline' },
                    }}
                  >
                    /year
                  </Box>
                </Typography>

                {/* Per Day Rate */}
                <Typography
                  variant='body2'
                  sx={{ color: themeColors.secondary, fontWeight: 600, mb: 2 }}
                >
                  {formatAed(enterprisePlan.priceAed / enterprisePlan.durationDays)}
                  /day
                </Typography>

                {/* Savings Badge */}
                {/* <Chip
                  label='Save 38%'
                  sx={{
                    bgcolor: themeColors.secondary,
                    color: themeColors.primary,
                    fontWeight: 700,
                    mb: 3,
                  }}
                /> */}

                {/* <Button
                  variant='contained'
                  fullWidth
                  size='large'
                  onClick={() =>
                    onSelectPlan(enterprisePlan.planCode || enterprisePlan.id)
                  }
                  sx={{
                    bgcolor: themeColors.success,
                    color: 'white',
                    py: 1.5,
                    fontWeight: 700,
                    '&:hover': {
                      bgcolor: '#3a9668',
                    },
                  }}
                >
                  Subscribe Now
                </Button> */}
              </Box>
            </Paper>

            {/* Contact Box */}
            <Paper
              elevation={3}
              sx={{
                p: { xs: 3, md: 4 },
                background: 'linear-gradient(135deg, #232536 0%, #1a1c2e 100%)',
                color: 'white',
                borderRadius: 3,
                textAlign: 'center',
              }}
            >
              <Typography variant='body1' sx={{ mb: 3, opacity: 0.9 }}>
                Get in touch with us for custom pricing based on your needs
              </Typography>

              <Button
                variant='contained'
                size='large'
                fullWidth
                startIcon={<Email />}
                href='mailto:support@gcctaxlaws.com?subject=Enterprise%20Subscription%20Inquiry'
                sx={{
                  bgcolor: themeColors.secondary,
                  color: themeColors.primary,
                  py: 1.5,
                  fontWeight: 700,
                  transition: 'background-color 0.2s ease, color 0.2s ease',
                  '&:hover': {
                    bgcolor: themeColors.hover,
                    color: themeColors.secondary,
                  },
                }}
              >
                Contact Us
              </Button>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  )
}

// Feature Comparison Table
const FeatureComparisonTable = ({
  isMobile,
  isTablet,
  domainName,
  isGccDomain,
  sx, // Now accepting the sx prop
}) => {
  const mainAccessLabel = isGccDomain
    ? 'All GCC Access'
    : `${domainName} Access`
  const comparisonData = useMemo(
    () => getFeatureComparisonData(domainName, isGccDomain),
    [domainName, isGccDomain],
  )

  // Mobile/Tablet View (unchanged logic, but adding sx support)
  if (isMobile || isTablet) {
    return (
      <Stack spacing={3} sx={{ ...sx }}>
        {comparisonData.map((row, index) => (
          <Paper
            key={index}
            sx={{ p: 2, borderRadius: 2, bgcolor: themeColors.primary }}
          >
            <Typography
              variant='subtitle2'
              sx={{
                fontWeight: 700,
                mb: 2,
                color: '#ffda1b',
                textAlign: 'center',
              }}
            >
              {row.feature}
            </Typography>
            <Grid container spacing={2} justifyContent='center'>
              <Grid
                item
                xs={6}
                sx={{
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant='caption'
                  sx={{ color: 'rgba(255,255,255,0.7)' }}
                >
                  {mainAccessLabel}
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ fontWeight: 600, color: themeColors.secondary }}
                >
                  {row.allAccess}
                </Typography>
              </Grid>
              <Grid
                item
                xs={6}
                sx={{
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant='caption'
                  sx={{ color: 'rgba(255,255,255,0.7)' }}
                >
                  Free
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ fontWeight: 600, color: themeColors.secondary }}
                >
                  {row.free}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        ))}
      </Stack>
    )
  }

  // Desktop View - Now using actual Table components
  return (
    <TableContainer sx={{ ...sx }}>
      <Table sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
        <TableHead>
          <TableRow>
            <TableCell>Feature</TableCell>
            <TableCell>{mainAccessLabel}</TableCell>
            {/* <TableCell>DTAA Only</TableCell> */}
            <TableCell>Free</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {comparisonData.map((row, index) => (
            <TableRow key={index}>
              <TableCell>{row.feature}</TableCell>
              <TableCell>{row.allAccess}</TableCell>
              {/* <TableCell>{row.dtaa}</TableCell> */}
              <TableCell>{row.free}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
// Trust Signals Component
const TrustSignals = ({ isMobile }) => {
  const signals = [
    {
      icon: Lock,
      title: 'Secure Payments',
      description: 'All transactions encrypted with SSL',
    },
    {
      icon: Speed,
      title: 'Instant Access',
      description: 'Start using immediately after purchase',
    },
    {
      icon: Support,
      title: '24/7 Email Support',
      description: 'Help available whenever you need it',
    },
  ]

  return (
    <Box sx={{ mt: 8 }}>
      <Box
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: 920, lg: 980 },
          mx: 'auto',
          px: { xs: 2, sm: 0 },
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'stretch',
          gap: { xs: 4, sm: 3, md: 5 },
        }}
      >
        {signals.map((signal) => {
          const IconComponent = signal.icon
          return (
            <Box
              key={signal.title}
              sx={{
                width: { xs: '100%', sm: 260 },
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <IconComponent
                sx={{
                  fontSize: { xs: 40, md: 48 },
                  color: themeColors.primary,
                  mb: 1,
                }}
              />
              <Typography variant='h6' sx={{ fontWeight: 600, textAlign: 'center' }}>
                {signal.title}
              </Typography>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ textAlign: 'center', maxWidth: 260, mx: 'auto' }}
              >
                {signal.description}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
export default Pricing
